import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Button, Badge, Alert, Modal, Spinner
} from 'react-bootstrap';
import { BackupInfo, BackupListResponse, BackupStatsResponse } from '../types/backup';
import { backupService } from '../services/api/backup.service';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Database,
  Clock,
  HardDrive,
  RefreshCw,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

/**
 * Backup Manager Component
 * 
 * Main component for managing database backups.
 * Displays list of backups, allows creating new backups, restoring, and deleting.
 */
export const BackupManager: React.FC = () => {
  const navigate = useNavigate();
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [stats, setStats] = useState<BackupStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<boolean>(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState<{
    title: string;
    message: string;
    type: 'restore' | 'delete' | 'create';
    filename?: string;
    confirmText: string;
    onConfirm: () => void;
  } | null>(null);

  // Result dialog state
  const [showResultDialog, setShowResultDialog] = useState<boolean>(false);
  const [resultDialogProps, setResultDialogProps] = useState<{
    title: string;
    message: string;
    success: boolean;
    details?: string;
  } | null>(null);

  // Fetch backups and stats
  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [backupsResponse, statsResponse] = await Promise.all([
        backupService.getBackups(),
        backupService.getBackupStats()
      ]);
      
      setBackups(backupsResponse.backups || []);
      setStats(backupsResponse.count > 0 ? statsResponse : null);
    } catch (err) {
      console.error('Error fetching backups:', err);
      setError('Failed to load backups. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  // Handle create backup
  const handleCreateBackup = () => {
    setConfirmDialogProps({
      title: 'Create New Backup',
      message: 'Are you sure you want to create a new backup of the database? This may take a few minutes to complete.',
      type: 'create',
      confirmText: 'Create Backup',
      onConfirm: createBackup
    });
    setShowConfirmDialog(true);
  };

  const createBackup = async () => {
    setShowConfirmDialog(false);
    try {
      setCreating(true);
      setError(null);
      
      const response = await backupService.createBackup({ compress: true });
      
      if (response.success) {
        setResultDialogProps({
          title: 'Backup Created',
          message: `Backup created successfully: ${response.filename}`,
          success: true,
          details: response.message || `Size: ${response.backup?.size_human || 'unknown'}`
        });
      } else {
        setResultDialogProps({
          title: 'Backup Failed',
          message: response.error || 'Failed to create backup',
          success: false
        });
      }
      setShowResultDialog(true);
    } catch (err) {
      console.error('Error creating backup:', err);
      setResultDialogProps({
        title: 'Backup Failed',
        message: 'An error occurred while creating the backup.',
        success: false
      });
      setShowResultDialog(true);
    } finally {
      setCreating(false);
      fetchBackups();
    }
  };

  // Handle restore backup
  const handleRestoreBackup = (filename: string) => {
    setConfirmDialogProps({
      title: 'Restore Database',
      message: `WARNING: This will OVERWRITE all existing data in the database with the backup from ${format(new Date(filename.split('_')[2].split('.')[0]), 'yyyy-MM-dd HH:mm:ss')}. This action cannot be undone.`,
      type: 'restore',
      filename,
      confirmText: 'Restore (DANGER)',
      onConfirm: () => restoreBackup(filename)
    });
    setShowConfirmDialog(true);
  };

  const restoreBackup = async (filename: string) => {
    setShowConfirmDialog(false);
    try {
      setRestoring(filename);
      setError(null);
      
      const response = await backupService.restoreBackup(filename, true);
      
      if (response.success) {
        setResultDialogProps({
          title: 'Restore Completed',
          message: `Database restored successfully from: ${response.filename}`,
          success: true,
          details: `Restored at: ${response.restored_at || new Date().toISOString()}`
        });
      } else {
        setResultDialogProps({
          title: 'Restore Failed',
          message: response.error || 'Failed to restore backup',
          success: false,
          details: response.stderr || response.stdout || ''
        });
      }
      setShowResultDialog(true);
    } catch (err) {
      console.error('Error restoring backup:', err);
      setResultDialogProps({
        title: 'Restore Failed',
        message: 'An error occurred while restoring the backup.',
        success: false
      });
      setShowResultDialog(true);
    } finally {
      setRestoring(null);
      fetchBackups();
    }
  };

  // Handle delete backup
  const handleDeleteBackup = (filename: string) => {
    setConfirmDialogProps({
      title: 'Delete Backup',
      message: `Are you sure you want to permanently delete the backup: ${filename}? This action cannot be undone.`,
      type: 'delete',
      filename,
      confirmText: 'Delete Permanently',
      onConfirm: () => deleteBackup(filename)
    });
    setShowConfirmDialog(true);
  };

  const deleteBackup = async (filename: string) => {
    setShowConfirmDialog(false);
    try {
      setDeleting(filename);
      setError(null);
      
      const response = await backupService.deleteBackup(filename, true);
      
      if (response.success) {
        setResultDialogProps({
          title: 'Backup Deleted',
          message: `Backup deleted successfully: ${response.filename}`,
          success: true
        });
      } else {
        setResultDialogProps({
          title: 'Delete Failed',
          message: response.error || 'Failed to delete backup',
          success: false
        });
      }
      setShowResultDialog(true);
    } catch (err) {
      console.error('Error deleting backup:', err);
      setResultDialogProps({
        title: 'Delete Failed',
        message: 'An error occurred while deleting the backup.',
        success: false
      });
      setShowResultDialog(true);
    } finally {
      setDeleting(null);
      fetchBackups();
    }
  };

  // Check if action is disabled
  const isActionDisabled = useCallback((filename?: string) => {
    return creating || restoring !== null || deleting !== null || (filename && restoring === filename);
  }, [creating, restoring, deleting]);

  // Helper to get action button variant
  const getRestoreButtonVariant = (filename: string) => {
    if (restoring === filename) return 'primary';
    return 'outline-primary';
  };

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            onClick={() => navigate(-1)}
            disabled={isActionDisabled() as boolean}
            className="d-flex align-items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="h3 mb-0 d-flex align-items-center gap-2">
              <Database className="h-6 w-6" />
              Database Backups
            </h1>
            <p className="text-muted small mb-0">
              Manage database backups - view, create, restore, and delete
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={fetchBackups}
            disabled={isActionDisabled() as boolean}
            className="d-flex align-items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'spinner-border spinner-border-sm' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateBackup}
            disabled={isActionDisabled() as boolean}
            className="d-flex align-items-center gap-2"
          >
            <HardDrive className="h-4 w-4" />
            Create Backup
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <AlertTriangle className="h-4 w-4 me-2" />
          {error}
        </Alert>
      )}

      {/* Stats Card */}
      {stats && (
        <Card className="mb-4">
          <Card.Header>
            <Card.Title className="d-flex align-items-center gap-2">
              <HardDrive className="h-5 w-5" />
              Backup Statistics
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <div className="row">
              <div className="col-6 col-md-3 mb-3 mb-md-0">
                <div className="border rounded p-3">
                  <div className="h4 mb-0">{stats.total_backups}</div>
                  <div className="small text-muted">Total Backups</div>
                </div>
              </div>
              <div className="col-6 col-md-3 mb-3 mb-md-0">
                <div className="border rounded p-3">
                  <div className="h4 mb-0">{stats.total_size_human}</div>
                  <div className="small text-muted">Total Size</div>
                </div>
              </div>
              <div className="col-6 col-md-3 mb-3 mb-md-0">
                <div className="border rounded p-3">
                  <div className="h5 mb-0 truncate">{stats.newest_backup || 'N/A'}</div>
                  <div className="small text-muted">Newest Backup</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border rounded p-3">
                  <div className="h5 mb-0 truncate">{stats.oldest_backup || 'N/A'}</div>
                  <div className="small text-muted">Oldest Backup</div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Backups Table */}
      <Card>
        <Card.Header>
          <Card.Title className="d-flex align-items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Available Backups
          </Card.Title>
          <Card.Text>
            {backups.length} backups found{stats && ` - Total size: ${stats.total_size_human}`}
          </Card.Text>
        </Card.Header>
        <Card.Body>
          {loading && backups.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center py-5">
              <div className="d-flex flex-column align-items-center gap-3">
                <Spinner animation="border" variant="primary" />
                <p>Loading backups...</p>
              </div>
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-5">
              <Database className="h-8 w-8 mx-auto text-muted mb-3" />
              <p className="text-muted">No backups found</p>
              <p className="small text-muted mt-2">
                Create your first backup to get started
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Size</th>
                    <th>Created</th>
                    <th>Age</th>
                    <th>Type</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup) => {
                    const isDisabled = isActionDisabled(backup.filename);
                    const isRestoring = restoring === backup.filename;
                    const isDeleting = deleting === backup.filename;

                    return (
                      <tr key={backup.filename}>
                        <td className="font-monospace small">
                          <div className="truncate" style={{ maxWidth: '200px' }}>
                            {backup.filename}
                          </div>
                        </td>
                        <td>
                          <Badge bg="secondary">{backup.size_human}</Badge>
                        </td>
                        <td>
                          {format(new Date(backup.created), 'yyyy-MM-dd HH:mm:ss')}
                        </td>
                        <td>
                          {backupService.getBackupAge(backup.created)}
                        </td>
                        <td>
                          <Badge bg={backup.compressed ? 'primary' : 'light'} text={backup.compressed ? 'white' : 'dark' as any}>
                            {backup.compressed ? 'Compressed' : 'Plain SQL'}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              size="sm"
                              variant={getRestoreButtonVariant(backup.filename)}
                              onClick={() => handleRestoreBackup(backup.filename)}
                              disabled={(isDisabled || isRestoring) as boolean}
                              className="d-flex align-items-center gap-1"
                            >
                              {isRestoring ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <RefreshCw className="h-3 w-3" />
                              )}
                              Restore
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDeleteBackup(backup.filename)}
                              disabled={(isDisabled || isDeleting) as boolean}
                              className="d-flex align-items-center gap-1"
                            >
                              {isDeleting ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <Trash2 className="h-3 w-3" />
                              )}
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        
        {backups.length > 0 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <span className="small text-muted">
              Showing {backups.length} backups
            </span>
            <Button
              variant="outline-secondary"
              onClick={fetchBackups}
              disabled={isActionDisabled() as boolean}
              className="d-flex align-items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'spinner-border spinner-border-sm' : ''}`} />
              Refresh List
            </Button>
          </Card.Footer>
        )}
      </Card>

      {/* Confirmation Dialog */}
      <Modal show={showConfirmDialog} onHide={() => setShowConfirmDialog(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            {confirmDialogProps?.type === 'restore' && (
              <AlertTriangle className="h-5 w-5 text-danger" />
            )}
            {confirmDialogProps?.type === 'delete' && (
              <Trash2 className="h-5 w-5 text-danger" />
            )}
            {confirmDialogProps?.type === 'create' && (
              <HardDrive className="h-5 w-5" />
            )}
            {confirmDialogProps?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="whitespace-pre-wrap">{confirmDialogProps?.message}</p>
        </Modal.Body>
        <Modal.Footer className="d-flex gap-2">
          <Button variant="secondary" onClick={() => setShowConfirmDialog(false)}>
            Cancel
          </Button>
          <Button
            variant={confirmDialogProps?.type === 'delete' || confirmDialogProps?.type === 'restore' ? 'danger' : 'primary'}
            onClick={confirmDialogProps?.onConfirm}
            disabled={creating || restoring !== null || deleting !== null}
          >
            {confirmDialogProps?.confirmText}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Result Dialog */}
      <Modal show={showResultDialog} onHide={() => setShowResultDialog(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            {resultDialogProps?.success ? (
              <CheckCircle className="h-5 w-5 text-success" />
            ) : (
              <XCircle className="h-5 w-5 text-danger" />
            )}
            {resultDialogProps?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{resultDialogProps?.message}</p>
          {resultDialogProps?.details && (
            <pre className="small bg-light p-3 rounded mt-3 overflow-auto" style={{ maxHeight: '200px' }}>
              {resultDialogProps.details}
            </pre>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowResultDialog(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Loading overlay for operations */}
      {(creating || restoring !== null || deleting !== null) && (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-black bg-opacity-50 d-flex align-items-center justify-content-center" style={{ zIndex: 1050 }}>
          <div className="bg-white p-4 rounded border" style={{ maxWidth: '400px' }}>
            <div className="d-flex align-items-center gap-3">
              <Spinner animation="border" variant="primary" />
              <div>
                <h3 className="h5 mb-0">
                  {creating ? 'Creating Backup' : restoring ? 'Restoring Database' : deleting ? 'Deleting Backup' : 'Processing'}
                </h3>
                <p className="small text-muted mb-0">
                  {creating ? 'This may take a few minutes...' : 
                   restoring ? 'This will overwrite all existing data!' : 
                   'Please wait...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupManager;
